/**
 * Colecciones que la sección 7 marca como "igual que hoy" o nuevas pequeñas:
 * subscriptions, conversations/messages (+ translations, riskFlags), reviews, follows,
 * savedSearches, notifications, complianceCases, auditLog, otps/rateLimits (server-only).
 */
import { z } from 'zod';
import { SavedSearchFiltersSchema } from '../search/savedSearch.js';
import {
  ActorSchema,
  ConversationIdSchema,
  CountryCodeSchema,
  LanguageCodeSchema,
  MoneySchema,
  OrderIdSchema,
  ProductIdSchema,
  TimestampSchema,
  UserIdSchema,
  type Money,
} from './primitives.js';
import { ProSourceSchema, ProTierSchema } from './users.js';

// ---------- subscriptions/{uid} (unificado stripe / apple / google) ----------

export const SUBSCRIPTION_STATUSES = [
  'active',
  'trialing',
  'past_due',
  'cancelled',
  'expired',
  'paused',
] as const;
export const SubscriptionSchema = z.object({
  uid: UserIdSchema,
  source: ProSourceSchema,
  tier: ProTierSchema,
  product_id: z.string(),
  status: z.enum(SUBSCRIPTION_STATUSES),
  renews_at: TimestampSchema.nullable(),
  cancel_at: TimestampSchema.nullable(),
  /** Id en el origen: Stripe subscription id, RevenueCat original transaction id… */
  external_id: z.string(),
  last_event_id: z.string().nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type Subscription = z.infer<typeof SubscriptionSchema>;
export const subscriptionPath = (uid: string) => `subscriptions/${uid}` as const;

// ---------- conversations / messages ----------

export const ConversationSchema = z.object({
  id: ConversationIdSchema,
  buyer_id: UserIdSchema,
  seller_id: UserIdSchema,
  product_id: ProductIdSchema.nullable(),
  order_id: OrderIdSchema.nullable(),
  participants: z.array(UserIdSchema).length(2),
  last_message_preview: z.string().max(200),
  last_message_at: TimestampSchema,
  unread: z.record(z.string(), z.number().int().min(0)),
  created_at: TimestampSchema,
});
export type Conversation = z.infer<typeof ConversationSchema>;

export const MESSAGE_RISK_FLAGS = [
  'external_link',
  'off_platform_payment',
  'iban',
  'urgency',
  'impersonation',
  'phone_number',
  'email',
  /** 4/11: «tu artículo se ha vendido / pago hecho / escanea el QR» de otro usuario: fraude. */
  'fake_sale',
] as const;
export const MessageSchema = z.object({
  conversation_id: ConversationIdSchema,
  sender_id: UserIdSchema,
  text: z.string().max(4000),
  original_language: LanguageCodeSchema.nullable(),
  /** Traducción por idioma destino, guardada para no pagar dos veces (sección 13.7). */
  translations: z.record(LanguageCodeSchema, z.string()).optional(),
  // Lo pone el trigger `messaging-guard` después de crear el mensaje (las reglas prohíben que el
  // cliente lo escriba): hasta entonces el mensaje recién enviado no tiene el campo.
  risk_flags: z.array(z.enum(MESSAGE_RISK_FLAGS)).default([]),
  /** Fraude claro (suplantación + enlace): el texto no se enseña al destinatario. */
  hidden: z.boolean().optional(),
  attachments: z.array(z.object({ url: z.string().url(), kind: z.enum(['image', 'file']) })).max(5),
  /** Oferta en línea en el chat (sección 12). */
  offer_id: z.string().nullable(),
  created_at: TimestampSchema,
});
export type Message = z.infer<typeof MessageSchema>;

// ---------- monetización: featured / bumps / wardrobeSpotlights (1.0 featured_products, wardrobe_spotlights) ----------

export const MONETIZATION_SOURCES = [
  'card',
  'wallet',
  'pro_included',
  'pro_weekly',
  'admin',
  'app_store',
  'play_store',
] as const;
export const FeaturedSchema = z.object({
  id: z.string().min(1),
  product_id: ProductIdSchema,
  seller_id: UserIdSchema,
  pack_code: z.string().min(1),
  scope: z.enum(['one', 'all']),
  homepage: z.boolean(),
  country: z.string().length(2).nullable(),
  /** Palabras clave patrocinadas (solo PRO, máx. 10). */
  keywords: z.array(z.string().max(40)).max(10),
  source: z.enum(MONETIZATION_SOURCES),
  starts_at: TimestampSchema,
  expires_at: TimestampSchema,
  created_at: TimestampSchema,
});
export type Featured = z.infer<typeof FeaturedSchema>;

export const WardrobeSpotlightSchema = z.object({
  id: z.string().min(1),
  uid: UserIdSchema,
  /** Copia pública del vendedor al activarlo (la home no lee `users`). */
  username: z.string().nullable().optional(),
  avatar_url: z.string().nullable().optional(),
  country_code: z.string().nullable().optional(),
  active: z.boolean(),
  source: z.enum(MONETIZATION_SOURCES),
  starts_at: TimestampSchema,
  expires_at: TimestampSchema,
  created_at: TimestampSchema,
});
export type WardrobeSpotlight = z.infer<typeof WardrobeSpotlightSchema>;

// ---------- Foro (1.0 forum_categories / forum_threads / forum_posts; sección 10 C10) ----------

export const ForumCategorySchema = z.object({
  id: z.string().min(1), // = slug
  slug: z.string().min(1),
  icon: z.string(),
  order: z.number().int(),
  locked: z.boolean(),
  thread_count: z.number().int().min(0),
  last_activity: TimestampSchema.nullable(),
  created_at: TimestampSchema,
});
export type ForumCategory = z.infer<typeof ForumCategorySchema>;

export const FORUM_THREAD_STATUSES = ['open', 'locked', 'hidden'] as const;
export const ForumThreadSchema = z.object({
  id: z.string().min(1),
  category_id: z.string().min(1),
  /** Hilo oficial sembrado (`slug:key`), para resembrar sin duplicar. */
  seed_key: z.string().nullable(),
  author_uid: UserIdSchema,
  /** D16: en público solo el @usuario. */
  author_handle: z.string(),
  author_is_admin: z.boolean(),
  title: z.string().min(1).max(140),
  body: z.string().min(1).max(5000),
  original_language: z.string(),
  status: z.enum(FORUM_THREAD_STATUSES),
  reply_count: z.number().int().min(0),
  last_activity: TimestampSchema,
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type ForumThread = z.infer<typeof ForumThreadSchema>;

export const ForumPostSchema = z.object({
  id: z.string().min(1),
  thread_id: z.string().min(1),
  category_id: z.string().min(1),
  author_uid: UserIdSchema,
  author_handle: z.string(),
  author_is_admin: z.boolean(),
  body: z.string().min(1).max(5000),
  original_language: z.string(),
  status: z.enum(['visible', 'hidden']),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type ForumPost = z.infer<typeof ForumPostSchema>;

// ---------- D7: soporte (1.0 support_tickets / support_messages) ----------

export const SUPPORT_TICKET_STATUSES = [
  'open',
  'in_progress',
  'waiting_user',
  'resolved',
  'closed',
] as const;
export const SUPPORT_CATEGORIES = [
  'purchase',
  'sale',
  'payment',
  'shipping',
  'account',
  /** «Texto mal traducido» (20/10): sin bot; llega al admin con el idioma y la página. */
  'translation',
  'other',
] as const;
export const SupportTicketSchema = z.object({
  id: z.string().min(1),
  uid: UserIdSchema,
  subject: z.string().min(1).max(160),
  /** Asunto en español para el equipo (si el usuario no escribe en español). */
  subject_es: z.string().optional(),
  category: z.enum(SUPPORT_CATEGORIES),
  status: z.enum(SUPPORT_TICKET_STATUSES),
  language: z.string().min(2).max(5),
  /** Pedido relacionado (opcional; lo rellena el usuario o el enlace desde el pedido). */
  order_id: z.string().nullable(),
  /** Página desde la que se abrió soporte (útil en «texto mal traducido»). */
  page_url: z.string().max(500).nullable().optional(),
  /** El bot de primer nivel (D7) atiende hasta que el usuario pide una persona o el bot escala. */
  bot_active: z.boolean(),
  escalated_at: TimestampSchema.nullable(),
  last_reply_at: TimestampSchema,
  last_reply_by: z.enum(['user', 'bot', 'agent']),
  /** Primera respuesta humana (promesa de soporte). */
  first_agent_reply_at: TimestampSchema.nullable(),
  messages_count: z.number().int().min(0),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type SupportTicket = z.infer<typeof SupportTicketSchema>;

export const SupportMessageSchema = z.object({
  id: z.string().min(1),
  ticket_id: z.string().min(1),
  /** Dueño del ticket (para que las reglas dejen leer sin join). */
  uid: UserIdSchema,
  sender_role: z.enum(['user', 'bot', 'agent']),
  sender_uid: z.string().nullable(),
  content: z.string().min(1).max(4000),
  /** Español para el equipo (usuario/bot) u original del agente; `content` es lo que ve el usuario. */
  content_es: z.string().optional(),
  attachments: z.array(z.string().url()).max(5),
  created_at: TimestampSchema,
});
export type SupportMessage = z.infer<typeof SupportMessageSchema>;

/** `settings/supportStats` (1.0 platform_config/support_stats): promesa de soporte con datos reales. */
export const SupportStatsSchema = z.object({
  window_days: z.number().int(),
  tickets: z.number().int(),
  answered_pct: z.number(),
  median_first_reply_hours: z.number().nullable(),
  p90_first_reply_hours: z.number().nullable(),
  resolved_pct: z.number(),
  disputes: z.number().int(),
  disputes_resolved_pct: z.number(),
  median_dispute_days: z.number().nullable(),
  updated_at: TimestampSchema,
});
export type SupportStats = z.infer<typeof SupportStatsSchema>;

// ---------- B5: campañas de envío financiado («envío a 0,99 € hasta el domingo») ----------

export const ShippingCampaignSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(120),
  active: z.boolean(),
  /** Países de DESTINO (vacío = todos) y categorías raíz (vacío = todas). */
  countries: z.array(z.string().length(2)),
  category_ids: z.array(z.string().max(80)),
  /** Lo que paga el comprador como máximo, en EUR (0 = envío gratis). */
  buyer_price: MoneySchema,
  starts_at: TimestampSchema,
  ends_at: TimestampSchema,
  /** Presupuesto tope de lo que nuvvu asume, en EUR; al agotarse se desactiva sola. */
  budget: MoneySchema,
  spent: MoneySchema,
  orders_count: z.number().int().min(0),
  /** 2.1 §7: oferta exclusiva para socios del Club nuvvu (los demás no la ven). */
  club_only: z.boolean().optional(),
  created_by: UserIdSchema,
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type ShippingCampaign = z.infer<typeof ShippingCampaignSchema>;

// ---------- 2.1 §0/§7: campañas de descuento («sin comisión») y de cashback ----------

export const PROMO_CAMPAIGN_KINDS = ['protection_discount', 'cashback'] as const;
export const PromoCampaignKindSchema = z.enum(PROMO_CAMPAIGN_KINDS);
export type PromoCampaignKind = z.infer<typeof PromoCampaignKindSchema>;

/**
 * `promoCampaigns/{id}` — Black Friday y similares, pagado por nuvvu (el vendedor cobra lo mismo):
 *   protection_discount — el comprador paga `percent` % menos en la línea «Protección»
 *                         (protección + recargo); 100 = «sin comisión», como en la 1.0.
 *   cashback            — al completarse el pedido vuelve `percent` % del artículo como saldo de
 *                         premio (no retirable), con tope por pedido y caducidad opcional.
 * Los socios del Club entran `early_offer_days` antes; `club_only` = solo socios. Presupuesto en EUR
 * de lo que asume nuvvu; al agotarse se desactiva sola. Interruptores: promo_no_commission y
 * promo_cashback (con «solo admins» para probar en producción).
 */
export const PromoCampaignSchema = z.object({
  id: z.string().min(1),
  kind: PromoCampaignKindSchema,
  name: z.string().min(1).max(120),
  active: z.boolean(),
  /** Porcentaje (1–100): descuento de la protección o cashback sobre el artículo. */
  percent: z.number().int().min(1).max(100),
  /** Solo cashback: máximo por pedido en EUR (0 = sin tope). */
  max_per_order: MoneySchema,
  /** Solo cashback: días que dura el saldo devuelto (0 = no caduca). */
  credit_valid_days: z.number().int().min(0).max(730),
  /** Compra mínima (artículo) en EUR para entrar (0 = cualquiera). */
  min_item_price: MoneySchema,
  /** Países de DESTINO (vacío = todos) y categorías (vacío = todas). */
  countries: z.array(z.string().length(2)),
  category_ids: z.array(z.string().max(80)),
  starts_at: TimestampSchema,
  ends_at: TimestampSchema,
  budget: MoneySchema,
  spent: MoneySchema,
  orders_count: z.number().int().min(0),
  club_only: z.boolean().optional(),
  created_by: UserIdSchema,
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type PromoCampaign = z.infer<typeof PromoCampaignSchema>;

/** Cashback de un artículo: `percent` %, redondeado hacia abajo, con tope (0 = sin tope). */
export function cashbackFor(item: Money, percent: number, cap: Money | null): Money {
  let amount = Math.floor((Math.max(0, item.amount) * Math.max(0, percent)) / 100);
  if (cap && cap.amount > 0 && cap.currency === item.currency)
    amount = Math.min(amount, cap.amount);
  return { amount, currency: item.currency };
}

// ---------- reviews / follows / savedSearches / notifications ----------

export const ReviewSchema = z.object({
  order_id: OrderIdSchema,
  author_id: UserIdSchema,
  target_id: UserIdSchema,
  role: z.enum(['buyer', 'seller']),
  rating: z.number().int().min(1).max(5),
  text: z.string().max(2000),
  /** Feedback automático (sección 10, #9): positivo si el comprador calla 4 días; negativo si no envía. */
  automatic: z.boolean(),
  /** Instantánea del artículo (del pedido) para enseñar «qué compré / qué vendí». */
  product_id: ProductIdSchema.optional(),
  product_title: z.string().max(200).optional(),
  product_photo_url: z.string().url().nullable().optional(),
  created_at: TimestampSchema,
  /** Moderación (1.0 is_deleted; admin-moderateReview): oculta al público y fuera de la media. */
  hidden: z.boolean().optional(),
  hidden_reason: z.string().max(500).nullable().optional(),
  hidden_at: TimestampSchema.nullable().optional(),
});
export type Review = z.infer<typeof ReviewSchema>;

export const FollowSchema = z.object({
  follower_id: UserIdSchema,
  followed_id: UserIdSchema,
  created_at: TimestampSchema,
});

/** «Avísame de anuncios nuevos» (1.0 saved_searches): máx. MAX_SAVED_SEARCHES por usuario. */
export const SavedSearchSchema = z.object({
  uid: UserIdSchema,
  query: z.string().max(200),
  filters: SavedSearchFiltersSchema,
  notify: z.boolean(),
  last_notified_at: TimestampSchema.nullable(),
  notified_count: z.number().int().min(0).optional(),
  created_at: TimestampSchema,
});
export type SavedSearch = z.infer<typeof SavedSearchSchema>;

export const NOTIFICATION_KINDS = [
  'message',
  'offer',
  'order',
  'favorite_price_drop',
  'saved_search',
  'marketing',
  'system',
] as const;
export const NotificationSchema = z.object({
  uid: UserIdSchema,
  kind: z.enum(NOTIFICATION_KINDS),
  /** Clave i18n + variables: el texto se resuelve en el idioma del destinatario. */
  i18n_key: z.string(),
  vars: z.record(z.string(), z.union([z.string(), z.number()])),
  link: z.string().max(500).nullable(),
  read: z.boolean(),
  created_at: TimestampSchema,
});

// ---------- complianceCases (DSA / GPSR / DAC7) ----------

export const COMPLIANCE_CASE_KINDS = [
  'dsa_notice',
  'gpsr_authority_order',
  'dac7_report',
  'safety_gate_alert',
] as const;
export const COMPLIANCE_CASE_STATUSES = [
  'open',
  'in_review',
  'actioned',
  'rejected',
  'closed',
] as const;
export const ComplianceCaseSchema = z.object({
  id: z.string().min(1),
  kind: z.enum(COMPLIANCE_CASE_KINDS),
  status: z.enum(COMPLIANCE_CASE_STATUSES),
  subject: z.discriminatedUnion('type', [
    z.object({ type: z.literal('product'), product_id: ProductIdSchema }),
    z.object({ type: z.literal('user'), uid: UserIdSchema }),
    z.object({ type: z.literal('report'), year: z.number().int() }),
  ]),
  reporter: z
    .object({
      name: z.string().max(200),
      email: z.string().email().nullable(),
      is_authority: z.boolean(),
      uid: z.string().nullable().optional(),
      language: z.string().optional(),
    })
    .nullable(),
  description: z.string().max(8000),
  /** DSA: referencia corta para el denunciante y URL del contenido. */
  ref: z.string().optional(),
  location: z.string().optional(),
  /** DSA: respuesta motivada en plazo; GPSR: 2 días hábiles. */
  respond_by: TimestampSchema.nullable(),
  resolution_note: z.string().max(4000).nullable(),
  handled_by: UserIdSchema.nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type ComplianceCase = z.infer<typeof ComplianceCaseSchema>;

// ---------- auditLog (toda acción de admin y toda transición de pedido) ----------

export const AuditLogEntrySchema = z.object({
  at: TimestampSchema,
  actor: ActorSchema,
  action: z.string().min(1).max(80),
  target: z.object({ collection: z.string(), id: z.string() }),
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
  reason: z.string().max(1000).nullable(),
  amount: MoneySchema.nullable(),
  request_id: z.string().nullable(),
});
export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;

// ---------- server-only ----------

export const OtpSchema = z.object({
  channel: z.enum(['email', 'sms']),
  target_hash: z.string(),
  code_hash: z.string(),
  attempts: z.number().int().min(0),
  expires_at: TimestampSchema,
});

export const RateLimitSchema = z.object({
  key: z.string(),
  count: z.number().int().min(0),
  window_started_at: TimestampSchema,
});

// ---------- waitlist/{sha256(email)} (13b: "avísame cuando lleguéis a mi país") ----------

export const WaitlistEntrySchema = z.object({
  email: z.string().email(),
  country_code: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .nullable(),
  /** País de la IP en el momento del alta (puede diferir del declarado). */
  ip_country: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .nullable(),
  language: LanguageCodeSchema.nullable(),
  created_at: TimestampSchema,
  updated_at: TimestampSchema,
});
export type WaitlistEntry = z.infer<typeof WaitlistEntrySchema>;

// ---------- aiCache/{sha256} y aiUsage/{id} (sección 11: caché por hash, coste, interruptores) ----------

export const AI_FEATURES = [
  'support_bot',
  'forum_translation',
  'newsletter_translation',
  'social_copy',
  'dispute_assistant',
  'listing_from_photos',
  'listing_translation',
  'search_query_translation',
  'chat_translation',
  'price_suggestion',
  'import_classification',
  /** 4/11: primeros mensajes de cuentas nuevas (¿fraude?). */
  'chat_scam_detector',
  // 2.1 bloque C: vectores de anuncios (Vertex AI) y lo que los usa; revisión y respuestas (Haiku).
  'embeddings',
  'semantic_search',
  'seller_suggested_replies',
  'pre_moderation',
] as const;
export const AiFeatureSchema = z.enum(AI_FEATURES);
export type AiFeature = z.infer<typeof AiFeatureSchema>;

/** Resultado cacheado de una llamada de IA (id = sha256 de feature + modelo + entrada). */
export const AiCacheEntrySchema = z.object({
  feature: AiFeatureSchema,
  model: z.string(),
  value: z.unknown(),
  hits: z.number().int().min(0),
  created_at: TimestampSchema,
  last_hit_at: TimestampSchema,
});
export type AiCacheEntry = z.infer<typeof AiCacheEntrySchema>;

/** Una llamada real al proveedor (no las servidas desde caché): para el presupuesto mensual. */
export const AiUsageSchema = z.object({
  feature: AiFeatureSchema,
  model: z.string(),
  input_tokens: z.number().int().min(0),
  output_tokens: z.number().int().min(0),
  /** Coste estimado en milésimas de céntimo de EUR (µ€ no: 1/1000 de céntimo) según la tabla del modelo. */
  cost_millicents: z.number().int().min(0),
  duration_ms: z.number().int().min(0),
  uid: UserIdSchema.nullable(),
  ref: z.string().nullable(),
  created_at: TimestampSchema,
});
export type AiUsage = z.infer<typeof AiUsageSchema>;

// ---------- encuesta a vendedores (1.0 seller_surveys / seller_survey_responses) ----------

export const SellerSurveyOptionSchema = z.object({
  id: z.string().min(1).max(48),
  label: z.string().trim().min(1).max(120),
});
/** Una pregunta con 2–12 respuestas, por países, para vendedores con anuncio activo o para todos. */
export const SellerSurveySchema = z.object({
  id: z.string().min(1),
  question: z.string().trim().min(1).max(2000),
  question_image_url: z.string().url().startsWith('https://').nullable(),
  audience: z.enum(['sellers', 'all']),
  options: z.array(SellerSurveyOptionSchema).min(2).max(12),
  countries: z.array(CountryCodeSchema).min(1).max(40),
  active: z.boolean(),
  created_at: TimestampSchema,
  created_by: UserIdSchema,
  updated_at: TimestampSchema,
  updated_by: UserIdSchema,
});
export type SellerSurvey = z.infer<typeof SellerSurveySchema>;
export const SellerSurveyResponseSchema = z.object({
  id: z.string().min(1),
  survey_id: z.string().min(1),
  uid: UserIdSchema,
  country: CountryCodeSchema,
  option_id: z.string().min(1),
  option_label: z.string().max(120),
  created_at: TimestampSchema,
});
export type SellerSurveyResponse = z.infer<typeof SellerSurveyResponseSchema>;
